/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { App, Col, Form, FormProps, Input, Row, Switch, Upload } from 'antd';
import { Button } from '../button';
import ImgCrop from 'antd-img-crop';
import { UploadOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  createBannerSlider,
  getListBanner,
  resetMessages,
  watchBannerMessage,
  showPopup
} from '@/redux/slices/banner.slice';
import { setMessages } from '@/redux/slices/app.slice';
import { Message } from '@/enums/message.enum';
import { useQuery } from '@/hooks/query.hook';
import { PageURLs } from '@/utils/navigate';
import { useDrawer } from '@/hooks/drawer.hook';
import { BannerSliderDrawer } from '../drawer/banner-slider.drawer';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { useBeforeUnload } from 'react-router-dom';

type BannerSliderCreateFormProps = {
  id: string;
  handleGoBack: () => void;
};

export const BannerSliderCreateForm = ({
  id,
  handleGoBack
}: BannerSliderCreateFormProps) => {
  const [form] = Form.useForm();
  const messageInfo = useAppSelector(watchBannerMessage);
  const dispatch = useAppDispatch();
  const [fileList, setFileList] = useState<any[]>([]);
  const { push } = useQuery();
  const { modal } = App.useApp();
  const { openDrawer } = useDrawer();

  const onFinish: FormProps<CreateBannerProps>['onFinish'] = async (values) => {
    let body: any = {
      id_platform: id,
      is_active: values.isActive ? 'TRUE' : 'FALSE',
      redirect_url: values.redirectUrl,
      name: values.name
    };

    if (fileList.length > 0 && fileList[0].url) {
      body = {
        ...body,
        thumbnail: fileList[0].url as string
      };
    }

    await dispatch(createBannerSlider(body));
    await push(PageURLs.ofHomeBannerSlider());
    await dispatch(
      getListBanner({
        offset: PAGINATION_PARAMS.offset,
        limit: PAGINATION_PARAMS.limit,
        sort_by: 'ASC'
      })
    );
    if (showPopup) {
      modal.confirm({
        title: 'Sắp xếp thứ tự hiển thị Banner',
        content: <em>Bạn có muốn sắp xếp thứ tự hiển thị Banner không?</em>,
        okText: 'Có',
        width: 450,
        cancelText: 'Không',
        onOk: () => {
          openDrawer(<BannerSliderDrawer />);
          dispatch(showPopup(false));
        },
        onCancel: () => {
          dispatch(showPopup(false));
        }
      });
    }
  };

  const beforeUpload = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setFileList([{ url: reader.result }]);
    };

    return false;
  };

  const handleRemove = () => {
    setFileList([]);
    form.setFieldValue('imageUrl', undefined);
  };

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );
      if (messageInfo.type === Message.SUCCESS) {
        form.resetFields();
        dispatch(showPopup(true));
      }
      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo, form]);

  useBeforeUnload(
    (e) => {
      e.preventDefault();
    },
    { capture: true }
  );

  return (
    <Form
      form={form}
      name='create-banner'
      css={formStyles}
      initialValues={{ remember: true }}
      onFinish={onFinish}
      autoComplete='off'
      className='ant-form-custom'
    >
      <Form.Item<CreateBannerProps>
        label='Tên banner'
        name='name'
        rules={[{ required: true, message: 'Vui lòng nhập tên Banner' }]}
      >
        <Input css={inputStyles} size='large' placeholder='Nhập tên Banner' />
      </Form.Item>

      <Form.Item<CreateBannerProps>
        label='Hình ảnh'
        name='imageUrl'
        rules={[{ required: true, message: 'Vui lòng chọn ảnh' }]}
        getValueFromEvent={(e) => {
          if (Array.isArray(e)) {
            return e;
          }
          return e?.fileList || [];
        }}
        valuePropName='fileList'
      >
        <ImgCrop
          aspect={2.86 / 1}
          onModalOk={(value) => {
            form.setFieldValue('imageUrl', value);
            form.validateFields(['imageUrl']);
          }}
          rotationSlider
          modalTitle='Cắt ảnh'
          modalOk='Xác nhận'
          modalCancel='Hủy'
          beforeCrop={(file) => file}
        >
          <Upload
            fileList={fileList}
            maxCount={1}
            beforeUpload={beforeUpload}
            listType='picture'
            accept='.png, .jpeg, .jpg'
            onRemove={handleRemove}
          >
            <Button
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
              icon={<UploadOutlined />}
            >
              Tải lên hình ảnh
            </Button>
          </Upload>
        </ImgCrop>
      </Form.Item>

      <Form.Item<CreateBannerProps> label='URL' name='redirectUrl'>
        <Input css={inputStyles} size='large' placeholder='Nhập URL dẫn đến' />
      </Form.Item>

      <Form.Item<CreateBannerProps>
        label='Hiển thị'
        name='isActive'
        initialValue={true}
      >
        <Switch checkedChildren='On' unCheckedChildren='Off' defaultChecked />
      </Form.Item>

      <Row css={formBottomStyles} justify='end' gutter={[20, 0]}>
        <Col>
          <Button onClick={handleGoBack} danger shape='round'>
            Huỷ bỏ
          </Button>
        </Col>
        <Col>
          <Button
            css={actionBtnStyles}
            htmlType='submit'
            type='primary'
            shape='round'
          >
            Lưu
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const formStyles = css`
  margin-top: 3rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  .ant-form-item {
    .ant-form-item-label {
      width: 12rem;
      text-align: left;
      font-weight: 500;
      line-height: 5rem;
    }
    .ant-form-item-row {
      align-items: center;
    }
  }
`;

const formBottomStyles = css`
  margin-top: 2rem;
`;

const actionBtnStyles = css`
  width: 10rem;
  font-weight: 500;
`;

const inputStyles = css`
  height: 5rem;
`;
